import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';

import { MODAL_ANIMATION, MODAL_SIZES, MODAL_Z_INDEX } from './constants';
import { UnifiedModalContext } from './context';
import { ModalContextValue, ModalProps } from './types';

import { useDraggable } from '@controlrooms/hooks';

const ModalContainer = styled(motion.div)<{
  $height: string;
  $draggable: boolean;
}>`
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 100px);
  ${({ $height }) => {
    switch ($height) {
      case 'full':
        return 'height: calc(100vh - 100px);';
      case 'fixed':
        return 'height: 70vh;';
      default:
        return '';
    }
  }}
  border-radius: 4px;
  background: ${({ theme }) => theme.modal.contentBackgroundColor};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);

  ${({ $draggable }) =>
    $draggable &&
    css`
      cursor: default;
    `}
`;

const DragHandle = styled.div`
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  onCancel,
  onConfirm,
  size = 'lg',
  width,
  height = 'auto',
  draggable = false,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  loading = false,
  unmountOnExit = false,
  hideCloseButton = false,
  className,
  children,
}) => {
  const { position, onPointerDown, resetPosition } = useDraggable(draggable);
  const prevOpen = useRef(open);

  const dismiss = useCallback(() => {
    if (loading) return;
    (onCancel ?? onClose)?.();
  }, [onCancel, onClose, loading]);

  useEffect(() => {
    if (prevOpen.current && !open) {
      resetPosition();
    }
    prevOpen.current = open;
  }, [open, resetPosition]);

  const contextValue = useMemo<ModalContextValue>(
    () => ({
      dismiss,
      onConfirm,
      closeOnEsc,
      closeOnOverlayClick,
      loading,
      hideCloseButton,
    }),
    [dismiss, onConfirm, closeOnEsc, closeOnOverlayClick, loading, hideCloseButton],
  );

  const maxWidth = width ?? `${MODAL_SIZES[size]}px`;

  const modalStyles: ReactModal.Styles = {
    overlay: {
      position: 'fixed',
      zIndex: MODAL_Z_INDEX.modalOverlay,
      border: '0px',
      background: 'rgba(0, 0, 0, 0.6)',
      width: '100%',
      height: '100%',
      overflowY: 'auto',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
    },
    content: {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      outline: 'none',
      padding: '0px',
      borderRadius: '0px',
      border: 'none',
      overflow: 'visible',
      maxWidth,
      width: '100%',
      margin: '50px 0',
      background: 'unset',
      zIndex: MODAL_Z_INDEX.modal,
      transform: draggable ? `translate(${position.x}px, ${position.y}px)` : undefined,
    },
  };

  const handleRequestClose = useCallback(() => {
    if (loading) return;
    dismiss();
  }, [loading, dismiss]);

  if (unmountOnExit && !open) {
    return null;
  }

  return (
    <UnifiedModalContext.Provider value={contextValue}>
      <ReactModal
        isOpen={open}
        onRequestClose={handleRequestClose}
        shouldCloseOnOverlayClick={closeOnOverlayClick}
        shouldCloseOnEsc={closeOnEsc}
        style={modalStyles}
        appElement={document.getElementById('root') as HTMLElement}
      >
        <AnimatePresence>
          {open && (
            <ModalContainer
              $height={height}
              $draggable={draggable}
              className={className}
              initial={MODAL_ANIMATION.dialog.initial}
              animate={MODAL_ANIMATION.dialog.animate}
              exit={MODAL_ANIMATION.dialog.exit}
              transition={{ duration: MODAL_ANIMATION.duration }}
            >
              {draggable ? (
                <>
                  <DragHandle onPointerDown={onPointerDown}>
                    {React.Children.map(children, (child, index) => (index === 0 ? child : null))}
                  </DragHandle>
                  {React.Children.map(children, (child, index) => (index > 0 ? child : null))}
                </>
              ) : (
                children
              )}
            </ModalContainer>
          )}
        </AnimatePresence>
      </ReactModal>
    </UnifiedModalContext.Provider>
  );
};
